// src/helpers/driverScoreExport.js
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import dayjs from 'dayjs';
import { determineIndexName, determineIndexNameWithFallback, isEventEnabled } from './howenScoreHelper';
import { formatSecondsToDuration } from './driverScoreSummary';

// ==========================================
// CONSTANTS & STYLING TOKENS
// ==========================================

const FONT_NAME = 'Arial';
const FONT_SIZE_DEFAULT = 10;
const FONT_SIZE_TITLE = 14;
const FONT_SIZE_SECTION = 11;

const COLOR_HEADER_BG = 'FF3F4E8C'; // #3F4E8C
const COLOR_HEADER_FG = 'FFFFFFFF'; // White
const COLOR_BORDER = 'FFC9CEDC'; // #C9CEDC
const COLOR_ZEBRA_BG = 'FFF7F8FC'; // #F7F8FC
const COLOR_MUTED = 'FF666666';

const INDEX_COLORS = {
  A: { fg: 'FF1E7B34', bg: 'FFD8F0DC' }, // Green
  B: { fg: 'FF8A6D00', bg: 'FFFFF3C4' }, // Yellow
  C: { fg: 'FFB35900', bg: 'FFFFE0C2' }, // Orange
  D: { fg: 'FFA61B1B', bg: 'FFF8D2D2' }, // Red
  E: { fg: 'FFA61B1B', bg: 'FFF8D2D2' } // Red
};

const BORDER_THIN = {
  top: { style: 'thin', color: { argb: COLOR_BORDER } },
  left: { style: 'thin', color: { argb: COLOR_BORDER } },
  bottom: { style: 'thin', color: { argb: COLOR_BORDER } },
  right: { style: 'thin', color: { argb: COLOR_BORDER } }
};

// Summary Column Definitions (shared across Summary Excel, CSV, PDF, and Detail Sheet 1)
export const SUMMARY_COLUMNS = [
  { key: 'no', header: 'No', width: 6, align: 'center' },
  { key: 'period_start', header: 'Period Start', width: 20, align: 'center' },
  { key: 'period_end', header: 'Period End', width: 20, align: 'center' },
  { key: 'driver_code', header: 'Driver Code', width: 15, align: 'center' },
  { key: 'driver_name', header: 'Driver', width: 25, align: 'left', bold: true },
  { key: 'score_config', header: 'Score Config', width: 20, align: 'left' },
  { key: 'score_retase', header: 'Total Score (Retase)', width: 20, align: 'right', numFmt: '0.00' },
  { key: 'index_retase', header: 'Indeks (Retase)', width: 16, align: 'center', isIndex: true },
  { key: 'score_non_retase', header: 'Total Score (Non-Retase)', width: 24, align: 'right', numFmt: '0.00' },
  { key: 'index_non_retase', header: 'Indeks (Non-Retase)', width: 20, align: 'center', isIndex: true },
  { key: 'score_combine', header: 'Total Score (Combine)', width: 22, align: 'right', numFmt: '0.00' },
  { key: 'index_combine', header: 'Indeks (Combine)', width: 18, align: 'center', isIndex: true },
  { key: 'mileage', header: 'Total Mileage (KM)', width: 20, align: 'right', numFmt: '#,##0.00' },
  { key: 'travel_time', header: 'Total Travel Time', width: 18, align: 'center' },
  { key: 'stop_time', header: 'Total Stop Time', width: 18, align: 'center' }
];

// ==========================================
// STRING & NUMBER FORMATTING HELPERS
// ==========================================

/**
 * Format event keys to Title Case (e.g. "phone_call" -> "Phone Call")
 * @param {string} key
 * @returns {string}
 */
export function formatEventKey(key) {
  if (!key) return '';
  return String(key)
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Sanitize folder and filename parts:
 * - trim & replace consecutive whitespace with "-"
 * - remove illegal characters: \ / : * ? " < > | and comma
 * - collapse dashes & truncate to max 40 characters
 * @param {string} str
 * @returns {string}
 */
export function sanitizeFilenamePart(str) {
  if (!str) return '';
  let s = String(str).trim();
  s = s.replace(/\s+/g, '-');
  s = s.replace(/[\\/:*?"<>|,]/g, '');
  s = s.replace(/-+/g, '-');
  s = s.replace(/^-+|-+$/g, '');
  return s.slice(0, 40);
}

/**
 * Format duration values safely (numbers as seconds or formatted strings)
 * @param {string|number} val
 * @returns {string}
 */
export function formatDurationValue(val) {
  if (val == null || val === '' || val === '-') return '-';
  if (typeof val === 'string' && val.includes(':')) return val;
  const num = Number(val);
  if (!isNaN(num)) {
    return formatSecondsToDuration(num);
  }
  return String(val);
}

/**
 * Safe number formatting
 * @param {any} val
 * @returns {number|string}
 */
function safeNumber(val) {
  if (val == null || val === '' || val === '-') return '-';
  const num = Number(val);
  return isNaN(num) ? '-' : num;
}

/**
 * Download a Blob as a file in browser
 * @param {Blob} blob
 * @param {string} filename
 */
function downloadBlob(blob, filename) {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

// ==========================================
// FILENAME BUILDER & HEADER INFO
// ==========================================

/**
 * Build consistent export filename without extension
 * @param {'summary' | 'detail-single' | 'evidence-single' | 'detail'} type
 * @param {Array} rows
 * @param {Object} headerInfo
 * @param {string} [customTimestamp]
 * @returns {string}
 */
export function buildExportFilename(type, rows, headerInfo, customTimestamp) {
  const timestamp = customTimestamp || dayjs().format('YYYYMMDD-HHmm');

  if (type === 'summary') {
    const prefix = 'driver-score';
    let scope = 'AllCompanies';
    const companyNames = headerInfo?.companyNames || '';

    if (companyNames === 'All Companies' || !companyNames) {
      scope = 'AllCompanies';
    } else if (companyNames.includes(',')) {
      const count = companyNames
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean).length;
      scope = `${count}Companies`;
    } else {
      scope = sanitizeFilenamePart(companyNames) || 'AllCompanies';
    }

    return `${prefix}_${scope}_${timestamp}`;
  }

  if (type === 'detail-single' || type === 'evidence-single') {
    const prefix = type === 'evidence-single' ? 'driver-score-evidence' : 'driver-score-detail';
    const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : rows || {};
    const driverCode = sanitizeFilenamePart(row.driver_code || row.driver_driver_code || row.driver || 'code');
    const driverName = sanitizeFilenamePart(row.driver_name || row.driver_driver_name || 'name');
    return `${prefix}_${driverCode}_${driverName}_${timestamp}`;
  }

  if (type === 'detail') {
    const prefix = 'driver-score-detail';
    const uniqueDriverMap = new Map();

    (rows || []).forEach((row) => {
      const key = row.driver ?? row.driver_id ?? row.driver_name ?? 'unknown';
      if (!uniqueDriverMap.has(key)) {
        uniqueDriverMap.set(key, row);
      }
    });

    const uniqueCount = uniqueDriverMap.size || (rows || []).length;
    const scope = `${uniqueCount}Drivers`;
    return `${prefix}_${scope}_${timestamp}`;
  }

  return `driver-score_${timestamp}`;
}

/**
 * Get standard header info for exports
 * @param {Array} selectedCompanies
 * @param {Array} companyOptions
 * @param {string} rangeOption
 * @param {string|dayjs} fromDate
 * @param {string|dayjs} toDate
 * @returns {{ companyNames: string, period: string, exportDate: string, exporter: string, periodStart: string, periodEnd: string }}
 */
export function getExportHeaderInfo(selectedCompanies, companyOptions = [], rangeOption, fromDate, toDate) {
  let companyNames = 'All Companies';
  if (selectedCompanies && selectedCompanies.length > 0 && !selectedCompanies.includes('all')) {
    companyNames = selectedCompanies
      .map((id) => {
        const comp = companyOptions.find((c) => String(c.value) === String(id));
        return comp ? comp.label : id;
      })
      .join(', ');
  }

  let startDate = null;
  let endDate = null;
  let periodLabel = '';

  if (fromDate && toDate) {
    startDate = dayjs(fromDate);
    endDate = dayjs(toDate);
    if (rangeOption === 'today') {
      periodLabel = ' (Today)';
    } else if (rangeOption === 'week') {
      periodLabel = ' (Last Week)';
    } else if (rangeOption === 'month') {
      periodLabel = ' (Last Month)';
    }
  } else if (rangeOption === 'today') {
    startDate = dayjs().startOf('day');
    endDate = dayjs().endOf('day');
    periodLabel = ' (Today)';
  } else if (rangeOption === 'week') {
    startDate = dayjs().subtract(7, 'day').startOf('day');
    endDate = dayjs().endOf('day');
    periodLabel = ' (Last Week)';
  } else if (rangeOption === 'month') {
    startDate = dayjs().subtract(1, 'month').startOf('day');
    endDate = dayjs().endOf('day');
    periodLabel = ' (Last Month)';
  }

  const periodStartStr = startDate && startDate.isValid() ? startDate.format('DD MMM YYYY HH:mm') : '-';
  const periodEndStr = endDate && endDate.isValid() ? endDate.format('DD MMM YYYY HH:mm') : '-';
  const period =
    startDate && endDate ? `${startDate.format('DD MMM YYYY HH:mm')} s/d ${endDate.format('DD MMM YYYY HH:mm')}${periodLabel}` : '-';

  let exporter = 'Admin';
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      exporter = localStorage.getItem('name') || localStorage.getItem('username') || 'Admin';
    }
  } catch {}

  const exportDate = `${dayjs().format('DD MMM YYYY HH:mm')} oleh ${exporter}`;

  return {
    companyNames,
    period,
    periodStart: periodStartStr,
    periodEnd: periodEndStr,
    exportDate,
    exporter
  };
}

// ==========================================
// EXCELJS STYLING APPLIERS
// ==========================================

/**
 * Apply styling directly to an index cell (A, B, C, D, E)
 * @param {ExcelJS.Cell} cell
 * @param {string|any} indexValue
 */
export function applyIndexCellStyle(cell, indexValue) {
  if (!cell || indexValue == null) return;
  const key = String(indexValue).trim().toUpperCase();
  const color = INDEX_COLORS[key];
  if (color) {
    cell.font = {
      name: FONT_NAME,
      size: FONT_SIZE_DEFAULT,
      bold: true,
      color: { argb: color.fg }
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: color.bg }
    };
  }
}

/**
 * Configure worksheet view and page setup
 * @param {ExcelJS.Worksheet} worksheet
 * @param {number} [frozenCol] 0-indexed column split
 * @param {number} [frozenRow] 0-indexed row split (default 8)
 */
function applyWorksheetConfig(worksheet, frozenCol = 0, frozenRow = 8) {
  worksheet.views = [
    {
      showGridLines: false,
      state: frozenCol > 0 || frozenRow > 0 ? 'frozen' : 'normal',
      xSplit: frozenCol,
      ySplit: frozenRow
    }
  ];

  worksheet.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0
  };
}

/**
 * Add standard report header to worksheet (Rows 1-6)
 * @param {ExcelJS.Worksheet} worksheet
 * @param {string} title
 * @param {Object} headerInfo
 * @param {Object|null} driverInfo - { code, name } or null if Summary All Drivers
 * @param {number} lastColIndex - 1-based index of the last table column
 */
function addReportHeader(worksheet, title, headerInfo, driverInfo = null, lastColIndex = 15) {
  const lastColLetter = worksheet.getColumn(lastColIndex).letter || 'O';

  // Row 1: Title
  worksheet.mergeCells(`A1:${lastColLetter}1`);
  const titleCell = worksheet.getCell('A1');
  titleCell.value = title;
  titleCell.font = { name: FONT_NAME, size: FONT_SIZE_TITLE, bold: true, color: { argb: 'FF000000' } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
  worksheet.getRow(1).height = 24;

  // Row 2: Blank
  worksheet.getRow(2).height = 10;

  const addMetaRow = (rowNum, label, value) => {
    worksheet.mergeCells(`A${rowNum}:B${rowNum}`);
    const labelCell = worksheet.getCell(`A${rowNum}`);
    labelCell.value = label;
    labelCell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT, bold: true };
    labelCell.alignment = { vertical: 'middle', horizontal: 'left' };

    const endCol = lastColIndex >= 8 ? 'H' : lastColLetter;
    worksheet.mergeCells(`C${rowNum}:${endCol}${rowNum}`);
    const valCell = worksheet.getCell(`C${rowNum}`);
    valCell.value = `: ${value || '-'}`;
    valCell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT };
    valCell.alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.getRow(rowNum).height = 18;
  };

  if (driverInfo) {
    addMetaRow(3, 'Company', headerInfo?.companyNames);
    addMetaRow(4, 'Driver', `${driverInfo.code || '-'} - ${driverInfo.name || '-'}`);
    addMetaRow(5, 'Periode', headerInfo?.period);
    addMetaRow(6, 'Diekspor', headerInfo?.exportDate);
  } else {
    addMetaRow(3, 'Company', headerInfo?.companyNames);
    addMetaRow(4, 'Periode', headerInfo?.period);
    addMetaRow(5, 'Diekspor', headerInfo?.exportDate);
    worksheet.getRow(6).height = 10; // blank space
  }

  // Row 7: Blank
  worksheet.getRow(7).height = 10;
}

/**
 * Apply table header styling to Row 8
 * @param {ExcelJS.Worksheet} worksheet
 * @param {Array<string>} headers
 * @param {number} rowNum
 */
function applyTableHeader(worksheet, headers, rowNum = 8) {
  const row = worksheet.getRow(rowNum);
  row.values = headers;
  row.height = 31.95;

  headers.forEach((_, idx) => {
    const cell = row.getCell(idx + 1);
    cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT, bold: true, color: { argb: COLOR_HEADER_FG } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLOR_HEADER_BG }
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = BORDER_THIN;
  });
}

/**
 * Apply conditional formatting to index columns for a range (safe for all ExcelJS versions)
 * @param {ExcelJS.Worksheet} worksheet
 * @param {string} colLetter
 * @param {number} startRow
 * @param {number} endRow
 */
function applyIndexConditionalFormatting(worksheet, colLetter, startRow = 9, endRow = 1000) {
  if (startRow > endRow) return;

  try {
    if (typeof worksheet.addConditionalFormatting === 'function') {
      const rules = Object.entries(INDEX_COLORS).map(([idxLetter, color]) => ({
        type: 'cellIs',
        operator: 'equal',
        formulae: [`"${idxLetter}"`],
        style: {
          font: { name: FONT_NAME, size: FONT_SIZE_DEFAULT, bold: true, color: { argb: color.fg } },
          fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: color.bg }, fgColor: { argb: color.bg } }
        }
      }));

      worksheet.addConditionalFormatting({
        ref: `${colLetter}${startRow}:${colLetter}${endRow}`,
        rules
      });
    }
  } catch {
    // Graceful fallback: direct cell styling is already applied per-cell
  }
}

// ==========================================
// DATA ROW EXTRACTION HELPERS
// ==========================================

/**
 * Extract summary row values from driver data
 * @param {Object} row
 * @param {number} index
 * @param {Object} headerInfo
 * @returns {Array}
 */
function extractSummaryRowData(row, index, headerInfo) {
  const periodStart = row.start_date ? dayjs(row.start_date).format('DD MMM YYYY HH:mm') : headerInfo?.periodStart || '-';
  const periodEnd = row.end_date ? dayjs(row.end_date).format('DD MMM YYYY HH:mm') : headerInfo?.periodEnd || '-';
  const driverCode = row.driver_code || row.driver_driver_code || row.driver || '-';
  const driverName = row.driver_name || row.driver_driver_name || '-';
  const scoreConfig = row.score_config_name || row.score_config?.score_config_name || '-';

  const scoreRetase = safeNumber(row.total_score_retase);
  const indexRetase = row.idx_name_retase || '-';

  const scoreNonRetase = safeNumber(row.total_score_non_retase);
  const indexNonRetase = row.idx_name_non_retase || '-';

  const scoreCombine = safeNumber(row.pct_score ?? row.total_score);
  const indexCombine = row.idx_name || '-';

  const mileage = safeNumber(row.total_distance_km);
  const travelTime = formatDurationValue(row.trip_duration || row.total_trip_duration);
  const stopTime = formatDurationValue(row.stop_duration || row.total_stop_duration);

  return [
    index + 1,
    periodStart,
    periodEnd,
    driverCode,
    driverName,
    scoreConfig,
    scoreRetase,
    indexRetase,
    scoreNonRetase,
    indexNonRetase,
    scoreCombine,
    indexCombine,
    mileage,
    travelTime,
    stopTime
  ];
}

/**
 * Collect and deduplicate GPS evidence items
 * @param {Object} row
 * @param {(dayRow: Object) => boolean} [dailyFilterFn]
 * @returns {Array} GPS evidence rows
 */
export function collectGpsEvidenceRows(row, dailyFilterFn) {
  const allEventHolders = [];

  if (Array.isArray(row?._dailyBreakdown) && row._dailyBreakdown.length > 0) {
    row._dailyBreakdown.forEach((dayRow) => {
      if (!dailyFilterFn || dailyFilterFn(dayRow)) {
        if (Array.isArray(dayRow.events)) {
          dayRow.events.forEach((ev) => {
            if (Array.isArray(ev.evidence)) {
              allEventHolders.push({ event_key: ev.event_key, evidence: ev.evidence });
            }
          });
        }
      }
    });
  } else if (!dailyFilterFn || dailyFilterFn(row)) {
    if (Array.isArray(row?.events)) {
      row.events.forEach((ev) => {
        if (Array.isArray(ev.evidence)) {
          allEventHolders.push({ event_key: ev.event_key, evidence: ev.evidence });
        }
      });
    }
  }

  const seen = new Set();
  const collected = [];

  allEventHolders.forEach(({ event_key, evidence }) => {
    evidence.forEach((item) => {
      const rawStartTime = item.start_time || item.createtime;
      if (!rawStartTime) return;

      const dedupeKey = `${event_key || item.event_key || ''}_${rawStartTime}_${item.lat}_${item.lon}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      const rawEndTime = item.stop_time || item.end_time || item.endTime;
      const location = item.location || item.address || (item.lat != null && item.lon != null ? `${item.lat}, ${item.lon}` : '-');
      const speed = item.speed != null ? `${item.speed} km/h` : item.max_speed != null ? `${item.max_speed} km/h` : '-';
      const duration =
        item.duration != null ? formatDurationValue(item.duration) : item.total_duration != null ? `${item.total_duration}s` : '-';

      const startDateObj = dayjs(rawStartTime);
      const endDateObj = rawEndTime ? dayjs(rawEndTime) : null;

      collected.push({
        eventKey: event_key || item.event_key,
        date: startDateObj.isValid() ? startDateObj.format('DD MMM YYYY') : String(rawStartTime),
        event: formatEventKey(item.event_key || event_key),
        startTime: startDateObj.isValid() ? startDateObj.format('DD MMM YYYY HH:mm:ss') : String(rawStartTime),
        endTime: endDateObj && endDateObj.isValid() ? endDateObj.format('DD MMM YYYY HH:mm:ss') : '-',
        location,
        speed,
        duration,
        _rawTime: new Date(rawStartTime).getTime() || 0
      });
    });
  });

  collected.sort((a, b) => a._rawTime - b._rawTime);
  return collected;
}

/**
 * Collect and deduplicate Camera (VSS) evidence items
 * @param {Object} row
 * @param {(dayRow: Object) => boolean} [dailyFilterFn]
 * @returns {Array} Camera evidence rows
 */
export function collectVssEvidenceRows(row, dailyFilterFn) {
  const allViolations = [];

  if (Array.isArray(row?._dailyBreakdown) && row._dailyBreakdown.length > 0) {
    row._dailyBreakdown.forEach((dayRow) => {
      if (!dailyFilterFn || dailyFilterFn(dayRow)) {
        if (Array.isArray(dayRow._violationEvents)) {
          allViolations.push(...dayRow._violationEvents);
        }
      }
    });
  } else if (!dailyFilterFn || dailyFilterFn(row)) {
    if (Array.isArray(row?._violationEvents)) {
      allViolations.push(...row._violationEvents);
    }
  }

  const seen = new Set();
  const collected = [];

  allViolations.forEach((item) => {
    const k = item.event_key;
    const rawEventTime = item.event_time || item.start_time || item.createtime;
    if (!k || !rawEventTime) return;

    const vehicle = item.vehicle || item.plateNo || item.deviceName || row.vehicle_name || row.vehicle || '-';
    const violation = item.violation || item.alarmTypeValue || item.event_key || '-';

    const dedupeKey = `${rawEventTime}_${vehicle}_${violation}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);

    const startDateObj = dayjs(rawEventTime);

    collected.push({
      eventKey: k,
      date: startDateObj.isValid() ? startDateObj.format('DD MMM YYYY') : String(rawEventTime),
      event: formatEventKey(k),
      startTime: startDateObj.isValid() ? startDateObj.format('DD MMM YYYY HH:mm:ss') : String(rawEventTime),
      vehicle,
      violation,
      _rawTime: new Date(rawEventTime).getTime() || 0
    });
  });

  collected.sort((a, b) => a._rawTime - b._rawTime);
  return collected;
}

// ==========================================
// WORKBOOK 1 BUILDERS: DRIVER DETAIL (4 SHEETS)
// ==========================================

/**
 * Sheet 1: Summary Sheet for Single Driver or All Drivers
 * @param {ExcelJS.Workbook} workbook
 * @param {Array} rows
 * @param {Object} headerInfo
 * @param {Object|null} driverInfo
 */
function buildSummarySheet(workbook, rows, headerInfo, driverInfo = null) {
  const worksheet = workbook.addWorksheet('Summary');
  const headers = SUMMARY_COLUMNS.map((c) => c.header);

  // 1. Report Header
  addReportHeader(worksheet, 'Driver Score Summary', headerInfo, driverInfo, SUMMARY_COLUMNS.length);

  // 2. Table Header at Row 8
  applyTableHeader(worksheet, headers, 8);

  // 3. Add Data Rows starting at Row 9
  rows.forEach((r, idx) => {
    const rowValues = extractSummaryRowData(r, idx, headerInfo);
    const dataRow = worksheet.addRow(rowValues);
    const isZebra = idx % 2 === 1;

    dataRow.height = 20;

    SUMMARY_COLUMNS.forEach((colDef, cIdx) => {
      const cell = dataRow.getCell(cIdx + 1);
      cell.font = {
        name: FONT_NAME,
        size: FONT_SIZE_DEFAULT,
        bold: !!colDef.bold
      };
      cell.alignment = {
        vertical: 'middle',
        horizontal: colDef.align || 'left'
      };
      cell.border = BORDER_THIN;

      if (isZebra) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: COLOR_ZEBRA_BG }
        };
      }

      if (colDef.isIndex) {
        applyIndexCellStyle(cell, cell.value);
      }

      if (colDef.numFmt && typeof cell.value === 'number') {
        cell.numFmt = colDef.numFmt;
      }
    });
  });

  const lastDataRow = worksheet.lastRow ? worksheet.lastRow.number : 8;

  // AutoFilter & Freeze Panes
  worksheet.autoFilter = `A8:O${Math.max(lastDataRow, 8)}`;
  applyWorksheetConfig(worksheet, 6, 8); // Freeze pane at G9

  // Column Widths
  worksheet.columns = SUMMARY_COLUMNS.map((c) => ({ width: c.width }));

  // Conditional Formatting for Index columns: H, J, L
  if (lastDataRow >= 9) {
    applyIndexConditionalFormatting(worksheet, 'H', 9, lastDataRow);
    applyIndexConditionalFormatting(worksheet, 'J', 9, lastDataRow);
    applyIndexConditionalFormatting(worksheet, 'L', 9, lastDataRow);
  }

  // Small gray note below table
  // const noteRow = worksheet.addRow([]);
  const noteRow2 = worksheet.addRow(['Catatan: Nilai kosong (-) menunjukkan tidak ada data untuk kategori tersebut.']);
  noteRow2.getCell(1).font = { name: FONT_NAME, size: 9, italic: true, color: { argb: COLOR_MUTED } };
}

/**
 * Sheet 2: Detail Score Event Sheet
 * @param {ExcelJS.Workbook} workbook
 * @param {Object} row
 * @param {Object} headerInfo
 * @param {Object} driverInfo
 */
function buildDetailScoreEventSheet(workbook, row, headerInfo, driverInfo) {
  const worksheet = workbook.addWorksheet('Detail Score Event');

  const headers = [
    'No',
    'Driver Code',
    'Driver',
    'Kategori',
    'Score Config',
    'Score Kategori',
    'Indeks Kategori',
    'Event',
    'Score Event',
    'Max Score',
    'Indeks Event',
    'Count Pelanggaran',
    'Jumlah Evidence'
  ];

  addReportHeader(worksheet, 'Detail Score Event', headerInfo, driverInfo, headers.length);
  applyTableHeader(worksheet, headers, 8);

  const dailyBreakdown = Array.isArray(row._dailyBreakdown) && row._dailyBreakdown.length > 0 ? row._dailyBreakdown : [row];
  const isRetaseDay = (r) => Array.isArray(r?.retase?.retase) && r.retase.retase.length > 0;

  const hasRetase =
    dailyBreakdown.some(isRetaseDay) ||
    row.total_score_retase != null ||
    (Array.isArray(row.events_retase) && row.events_retase.length > 0);
  const hasNonRetase =
    dailyBreakdown.some((r) => !isRetaseDay(r)) ||
    row.total_score_non_retase != null ||
    (Array.isArray(row.events_non_retase) && row.events_non_retase.length > 0);
  const hasBoth = hasRetase && hasNonRetase;

  const categoryBlocks = [];
  if (hasBoth) {
    categoryBlocks.push({
      key: 'retase',
      label: 'Retase',
      score: safeNumber(row.total_score_retase),
      index: row.idx_name_retase || '-',
      events: row.events_retase || [],
      filterFn: isRetaseDay
    });
    categoryBlocks.push({
      key: 'non_retase',
      label: 'Non Retase',
      score: safeNumber(row.total_score_non_retase),
      index: row.idx_name_non_retase || '-',
      events: row.events_non_retase || [],
      filterFn: (r) => !isRetaseDay(r)
    });
    categoryBlocks.push({
      key: 'combine',
      label: 'Combine',
      score: safeNumber(row.pct_score ?? row.total_score),
      index: row.idx_name || '-',
      events: row.events || [],
      filterFn: () => true
    });
  } else if (hasRetase) {
    categoryBlocks.push({
      key: 'retase',
      label: 'Retase',
      score: safeNumber(row.total_score_retase),
      index: row.idx_name_retase || '-',
      events: row.events_retase || [],
      filterFn: isRetaseDay
    });
  } else if (hasNonRetase) {
    categoryBlocks.push({
      key: 'non_retase',
      label: 'Non Retase',
      score: safeNumber(row.total_score_non_retase),
      index: row.idx_name_non_retase || '-',
      events: row.events_non_retase || [],
      filterFn: (r) => !isRetaseDay(r)
    });
  } else {
    categoryBlocks.push({
      key: 'combine',
      label: 'Combine',
      score: safeNumber(row.pct_score ?? row.total_score),
      index: row.idx_name || '-',
      events: row.events || [],
      filterFn: () => true
    });
  }

  const scoreIndexes = row.score_indexes || row.idx_detail || row.score_config?.score_indexes;
  const scoreConfigName = row.score_config_name || row.score_config?.score_config_name || '-';
  const driverCode = driverInfo.code || '-';
  const driverName = driverInfo.name || '-';

  let rowCounter = 1;

  categoryBlocks.forEach((block, bIdx) => {
    const activeEvents = (block.events || []).filter((ev) => {
      const isEnabled = isEventEnabled(ev.event_key, row.score_config_events);
      const weight = Number(ev.weight || 0);
      return isEnabled && weight > 0;
    });

    const gpsEv = collectGpsEvidenceRows(row, block.filterFn);
    const vssEv = collectVssEvidenceRows(row, block.filterFn);

    const startRowIdx = worksheet.lastRow ? worksheet.lastRow.number + 1 : 9;
    const isZebra = bIdx % 2 === 1;

    if (activeEvents.length === 0) {
      const dataRow = worksheet.addRow([
        rowCounter++,
        driverCode,
        driverName,
        block.label,
        scoreConfigName,
        block.score,
        block.index,
        '-',
        '-',
        10,
        '-',
        0,
        '-'
      ]);
      dataRow.height = 20;
      dataRow.eachCell((cell, colNumber) => {
        cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT };
        cell.border = BORDER_THIN;
        if (isZebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ZEBRA_BG } };
        if (colNumber === 7) applyIndexCellStyle(cell, block.index);
      });
      return;
    }

    activeEvents.forEach((ev) => {
      const eventKeyFormatted = formatEventKey(ev.event_key);
      const scoreEvent = ev.score != null ? Number(Number(ev.score).toFixed(2)) : '-';
      const eventIndex = ev.score != null ? determineIndexName(Number(ev.score) * 10, scoreIndexes) : '-';
      const countPelanggaran = ev.event_count != null ? Number(ev.event_count) : 0;

      const gpsCount = gpsEv.filter((g) => g.eventKey === ev.event_key).length;
      const vssCount = vssEv.filter((v) => v.eventKey === ev.event_key).length;
      const totalEvidence = gpsCount + vssCount;
      const evidenceDisplay = totalEvidence > 0 ? totalEvidence : '-';

      const dataRow = worksheet.addRow([
        rowCounter++,
        driverCode,
        driverName,
        block.label,
        scoreConfigName,
        block.score,
        block.index,
        eventKeyFormatted,
        scoreEvent,
        10,
        eventIndex,
        countPelanggaran,
        evidenceDisplay
      ]);
      dataRow.height = 20;

      dataRow.eachCell((cell, colNumber) => {
        cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT, bold: colNumber === 3 };
        cell.border = BORDER_THIN;
        if (isZebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ZEBRA_BG } };

        // Alignments
        if ([1, 2, 4, 7, 11].includes(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'center' };
        else if ([6, 9, 10, 12, 13].includes(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'right' };
        else cell.alignment = { vertical: 'middle', horizontal: 'left' };

        // Number formats
        if (typeof cell.value === 'number' && [6, 9].includes(colNumber)) {
          cell.numFmt = '0.00';
        }

        // Direct index styling
        if (colNumber === 7) applyIndexCellStyle(cell, block.index);
        if (colNumber === 11) applyIndexCellStyle(cell, eventIndex);
      });
    });

    const endRowIdx = worksheet.lastRow.number;
    const blockRowCount = endRowIdx - startRowIdx + 1;

    // Merge columns A-G only if block > 1 row
    if (blockRowCount > 1) {
      ['A', 'B', 'C', 'D', 'E', 'F', 'G'].forEach((colLetter) => {
        worksheet.mergeCells(`${colLetter}${startRowIdx}:${colLetter}${endRowIdx}`);
        const cell = worksheet.getCell(`${colLetter}${startRowIdx}`);
        cell.alignment = {
          vertical: 'middle',
          horizontal: ['C', 'E', 'H'].includes(colLetter) ? 'left' : 'center'
        };
      });
    }
  });

  const lastDataRow = worksheet.lastRow ? worksheet.lastRow.number : 8;

  worksheet.autoFilter = `A8:M${Math.max(lastDataRow, 8)}`;
  applyWorksheetConfig(worksheet, 3, 8); // Freeze pane at D9

  worksheet.columns = [
    { width: 6 }, // No
    { width: 14 }, // Driver Code
    { width: 22 }, // Driver
    { width: 14 }, // Kategori
    { width: 20 }, // Score Config
    { width: 16 }, // Score Kategori
    { width: 16 }, // Indeks Kategori
    { width: 22 }, // Event
    { width: 14 }, // Score Event
    { width: 12 }, // Max Score
    { width: 14 }, // Indeks Event
    { width: 18 }, // Count Pelanggaran
    { width: 16 } // Jumlah Evidence
  ];

  if (lastDataRow >= 9) {
    applyIndexConditionalFormatting(worksheet, 'G', 9, lastDataRow);
    applyIndexConditionalFormatting(worksheet, 'K', 9, lastDataRow);
  }
}

/**
 * Sheet 3: Ringkasan Harian Sheet
 * @param {ExcelJS.Workbook} workbook
 * @param {Object} row
 * @param {Object} headerInfo
 * @param {Object} driverInfo
 */
function buildDailySummarySheet(workbook, row, headerInfo, driverInfo) {
  const worksheet = workbook.addWorksheet('Ringkasan Harian');

  const headers = [
    'No',
    'Driver Code',
    'Driver',
    'Tanggal',
    'Tipe',
    'Vehicle',
    'Score Config',
    'Index Score',
    'Score Harian',
    'Keterangan Pembulatan',
    'Jumlah Retase',
    'Event',
    'Weight',
    'Jumlah Pelanggaran',
    'Score',
    'Index Event'
  ];

  addReportHeader(worksheet, 'Ringkasan Harian', headerInfo, driverInfo, headers.length);
  applyTableHeader(worksheet, headers, 8);

  const scoreIndexes = row.score_indexes || row.idx_detail || row.score_config?.score_indexes;
  const scoreConfigName = row.score_config_name || row.score_config?.score_config_name || '-';
  const driverCode = driverInfo.code || '-';
  const driverName = driverInfo.name || '-';

  const allDailyRows = Array.isArray(row._dailyBreakdown) && row._dailyBreakdown.length > 0 ? [...row._dailyBreakdown] : [row];

  allDailyRows.sort((a, b) => {
    return dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf();
  });

  let rowCounter = 1;

  allDailyRows.forEach((dayRow, dIdx) => {
    const dateStr = dayRow.start_date ? dayjs(dayRow.start_date).format('DD MMM YYYY') : '-';
    const isRetase = Array.isArray(dayRow.retase?.retase) && dayRow.retase.retase.length > 0;
    const typeStr = isRetase ? 'Retase' : 'Non Retase';
    const vehicleStr = dayRow.vehicle_name || dayRow.vehicle || dayRow.vtds?.vehicle_name || row.vehicle_name || row.vehicle || '-';
    const dayScoreConfig = dayRow.score_config_name || scoreConfigName;

    const dailyScoreNum = dayRow.total_score != null ? Number(Number(dayRow.total_score).toFixed(2)) : null;
    const fallbackIdxResult = dailyScoreNum != null ? determineIndexNameWithFallback(dailyScoreNum, scoreIndexes) : null;
    const dailyIndexStr = dayRow.idx_name || dayRow.index_name || fallbackIdxResult?.idx_name || '-';

    let roundingNote = '-';
    if (dayRow.rounding_note || dayRow.rounding_label) {
      roundingNote = dayRow.rounding_note || dayRow.rounding_label;
    } else if (fallbackIdxResult && fallbackIdxResult.isEstimated) {
      roundingNote = `*Dibulatkan ${fallbackIdxResult.roundedDirection === 'up' ? 'ke atas' : 'ke bawah'}`;
    }

    const retaseCount = isRetase ? dayRow.retase.retase.length : '-';

    const activeEvents = (dayRow.events || []).filter((ev) => {
      const isEnabled = isEventEnabled(ev.event_key, row.score_config_events);
      const weight = Number(ev.weight || 0);
      return isEnabled && weight > 0;
    });

    const startRowIdx = worksheet.lastRow ? worksheet.lastRow.number + 1 : 9;
    const isZebra = dIdx % 2 === 1;

    if (activeEvents.length === 0) {
      const dataRow = worksheet.addRow([
        rowCounter++,
        driverCode,
        driverName,
        dateStr,
        typeStr,
        vehicleStr,
        dayScoreConfig,
        dailyIndexStr,
        dailyScoreNum != null ? dailyScoreNum : '-',
        roundingNote,
        retaseCount,
        '-',
        '-',
        0,
        '-',
        '-'
      ]);
      dataRow.height = 20;
      dataRow.eachCell((cell, colNumber) => {
        cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT };
        cell.border = BORDER_THIN;
        if (isZebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ZEBRA_BG } };
        if (colNumber === 8) applyIndexCellStyle(cell, dailyIndexStr);
      });
      return;
    }

    activeEvents.forEach((ev) => {
      const eventKeyFormatted = formatEventKey(ev.event_key);
      const weight = ev.weight != null ? Number(ev.weight) : '-';
      const eventViolations = ev.event_count != null ? Number(ev.event_count) : 0;
      const eventScoreNum = ev.score != null ? Number(Number(ev.score).toFixed(2)) : '-';
      const eventIndex = ev.score != null ? determineIndexName(Number(ev.score) * 10, scoreIndexes) : '-';

      const dataRow = worksheet.addRow([
        rowCounter++,
        driverCode,
        driverName,
        dateStr,
        typeStr,
        vehicleStr,
        dayScoreConfig,
        dailyIndexStr,
        dailyScoreNum != null ? dailyScoreNum : '-',
        roundingNote,
        retaseCount,
        eventKeyFormatted,
        weight,
        eventViolations,
        eventScoreNum,
        eventIndex
      ]);
      dataRow.height = 20;

      dataRow.eachCell((cell, colNumber) => {
        cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT, bold: colNumber === 3 };
        cell.border = BORDER_THIN;
        if (isZebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ZEBRA_BG } };

        // Alignment
        if ([1, 2, 4, 5, 8, 16].includes(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'center' };
        else if ([9, 11, 13, 14, 15].includes(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'right' };
        else cell.alignment = { vertical: 'middle', horizontal: 'left' };

        // Number formats
        if (typeof cell.value === 'number' && [9, 15].includes(colNumber)) {
          cell.numFmt = '0.00';
        }

        // Direct index styling
        if (colNumber === 8) applyIndexCellStyle(cell, dailyIndexStr);
        if (colNumber === 16) applyIndexCellStyle(cell, eventIndex);
      });
    });

    const endRowIdx = worksheet.lastRow.number;
    const dayRowCount = endRowIdx - startRowIdx + 1;

    // Merge columns A-K per day if day has > 1 row
    if (dayRowCount > 1) {
      ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'].forEach((colLetter) => {
        worksheet.mergeCells(`${colLetter}${startRowIdx}:${colLetter}${endRowIdx}`);
        const cell = worksheet.getCell(`${colLetter}${startRowIdx}`);
        cell.alignment = {
          vertical: 'middle',
          horizontal: ['C', 'F', 'G', 'J'].includes(colLetter) ? 'left' : 'center'
        };
      });
    }
  });

  const lastDataRow = worksheet.lastRow ? worksheet.lastRow.number : 8;

  worksheet.autoFilter = `A8:P${Math.max(lastDataRow, 8)}`;
  applyWorksheetConfig(worksheet, 4, 8); // Freeze pane at E9

  worksheet.columns = [
    { width: 6 }, // No
    { width: 14 }, // Driver Code
    { width: 22 }, // Driver
    { width: 15 }, // Tanggal
    { width: 14 }, // Tipe
    { width: 18 }, // Vehicle
    { width: 18 }, // Score Config
    { width: 14 }, // Index Score
    { width: 14 }, // Score Harian
    { width: 24 }, // Keterangan Pembulatan
    { width: 14 }, // Jumlah Retase
    { width: 22 }, // Event
    { width: 10 }, // Weight
    { width: 18 }, // Jumlah Pelanggaran
    { width: 12 }, // Score
    { width: 14 } // Index Event
  ];

  if (lastDataRow >= 9) {
    applyIndexConditionalFormatting(worksheet, 'H', 9, lastDataRow);
    applyIndexConditionalFormatting(worksheet, 'P', 9, lastDataRow);
  }
}

/**
 * Sheet 4: Retase Sheet
 * @param {ExcelJS.Workbook} workbook
 * @param {Object} row
 * @param {Object} headerInfo
 * @param {Object} driverInfo
 */
function buildRetaseSheet(workbook, row, headerInfo, driverInfo) {
  const worksheet = workbook.addWorksheet('Retase');

  const headers = [
    'No',
    'Tanggal',
    'Vehicle',
    'Retase Ke',
    'Trip Ke',
    'Waktu Mulai',
    'Waktu Selesai',
    'Asal',
    'Tujuan',
    'Jarak (KM)',
    'Durasi'
  ];

  addReportHeader(worksheet, 'Data Retase', headerInfo, driverInfo, headers.length);
  applyTableHeader(worksheet, headers, 8);

  const allDailyRows = Array.isArray(row._dailyBreakdown) && row._dailyBreakdown.length > 0 ? [...row._dailyBreakdown] : [row];
  allDailyRows.sort((a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf());

  let rowCounter = 1;
  let totalRetaseRows = 0;

  allDailyRows.forEach((dayRow, dIdx) => {
    const dateStr = dayRow.start_date ? dayjs(dayRow.start_date).format('DD MMM YYYY') : '-';
    const vehicleStr = dayRow.vehicle_name || dayRow.vehicle || dayRow.vtds?.vehicle_name || row.vehicle_name || row.vehicle || '-';
    const retaseList = Array.isArray(dayRow.retase?.retase)
      ? dayRow.retase.retase
      : Array.isArray(dayRow.retase?.route_sort)
        ? dayRow.retase.route_sort
        : Array.isArray(dayRow.route_sort)
          ? dayRow.route_sort
          : Array.isArray(dayRow.retase)
            ? dayRow.retase
            : [];

    retaseList.forEach((retaseItem, rIdx) => {
      const retaseNumber = rIdx + 1;
      const trips =
        Array.isArray(retaseItem.details) && retaseItem.details.length > 0
          ? retaseItem.details
          : Array.isArray(retaseItem.route_sort) && retaseItem.route_sort.length > 0
            ? retaseItem.route_sort
            : Array.isArray(retaseItem.trips) && retaseItem.trips.length > 0
              ? retaseItem.trips
              : [retaseItem];

      // Extract default origin and destination from retaseItem routes if available
      const retaseRoutes =
        Array.isArray(retaseItem.routes) && retaseItem.routes.length > 0
          ? retaseItem.routes
          : Array.isArray(retaseItem.route) && retaseItem.route.length > 0
            ? retaseItem.route
            : [];

      let retaseOriginStr = '-';
      let retaseDestStr = '-';

      if (retaseRoutes.length > 0) {
        const sortedRoutes = [...retaseRoutes].sort((a, b) => (Number(a.sequence_number) || 0) - (Number(b.sequence_number) || 0));
        const firstRoute = sortedRoutes[0];
        const lastRoute = sortedRoutes[sortedRoutes.length - 1];
        retaseOriginStr =
          firstRoute?.spatial_name || firstRoute?.spatialName || firstRoute?.name || firstRoute?.geofence_name || firstRoute?.label || '-';
        retaseDestStr =
          lastRoute?.spatial_name || lastRoute?.spatialName || lastRoute?.name || lastRoute?.geofence_name || lastRoute?.label || '-';
      }

      if (retaseOriginStr === '-') {
        if (typeof retaseItem.origin === 'object' && retaseItem.origin !== null) {
          retaseOriginStr =
            retaseItem.origin.name || retaseItem.origin.label || retaseItem.origin.spatial_name || retaseItem.origin.address || '-';
        } else {
          retaseOriginStr = retaseItem.origin || retaseItem.origin_name || retaseItem.from || retaseItem.start_location || '-';
        }
      }

      if (retaseDestStr === '-') {
        if (typeof retaseItem.destination === 'object' && retaseItem.destination !== null) {
          retaseDestStr =
            retaseItem.destination.name ||
            retaseItem.destination.label ||
            retaseItem.destination.spatial_name ||
            retaseItem.destination.address ||
            '-';
        } else {
          retaseDestStr = retaseItem.destination || retaseItem.destination_name || retaseItem.to || retaseItem.end_location || '-';
        }
      }

      trips.forEach((trip, tIdx) => {
        totalRetaseRows++;
        const tripNumber = tIdx + 1;

        let originStr = retaseOriginStr;
        let destStr = retaseDestStr;

        if (Array.isArray(trip.routes) && trip.routes.length > 0) {
          const sortedTripRoutes = [...trip.routes].sort((a, b) => (Number(a.sequence_number) || 0) - (Number(b.sequence_number) || 0));
          const firstR = sortedTripRoutes[0];
          const lastR = sortedTripRoutes[sortedTripRoutes.length - 1];
          originStr = firstR?.spatial_name || firstR?.spatialName || firstR?.name || firstR?.geofence_name || firstR?.label || originStr;
          destStr = lastR?.spatial_name || lastR?.spatialName || lastR?.name || lastR?.geofence_name || lastR?.label || destStr;
        } else {
          if (typeof trip.origin === 'object' && trip.origin !== null) {
            originStr = trip.origin.name || trip.origin.label || trip.origin.address || trip.origin.spatial_name || originStr;
          } else if (trip.origin || trip.origin_name || trip.from || trip.start_location) {
            originStr = trip.origin || trip.origin_name || trip.from || trip.start_location;
          }

          if (typeof trip.destination === 'object' && trip.destination !== null) {
            destStr =
              trip.destination.name || trip.destination.label || trip.destination.address || trip.destination.spatial_name || destStr;
          } else if (trip.destination || trip.destination_name || trip.to || trip.end_location) {
            destStr = trip.destination || trip.destination_name || trip.to || trip.end_location;
          }
        }

        const rawStart =
          trip.start ||
          trip.start_time ||
          trip.startTime ||
          trip.start_date ||
          trip.startDate ||
          trip.details?.[0]?.start ||
          retaseItem.start ||
          retaseItem.start_time ||
          retaseItem.startTime ||
          retaseItem.details?.[0]?.start;
        const rawEnd =
          trip.end ||
          trip.end_time ||
          trip.endTime ||
          trip.end_date ||
          trip.endDate ||
          trip.details?.[0]?.end ||
          retaseItem.end ||
          retaseItem.end_time ||
          retaseItem.endTime ||
          retaseItem.details?.[0]?.end;
        const startTimeStr = rawStart && dayjs(rawStart).isValid() ? dayjs(rawStart).format('DD MMM YYYY HH:mm') : '-';
        const endTimeStr = rawEnd && dayjs(rawEnd).isValid() ? dayjs(rawEnd).format('DD MMM YYYY HH:mm') : '-';

        const distanceRaw =
          trip.distance ??
          trip.dist ??
          trip.distance_km ??
          trip.km ??
          trip.details?.[0]?.distance ??
          trip.details?.[0]?.dist ??
          retaseItem.total_distance_km ??
          retaseItem.distance ??
          retaseItem.dist ??
          retaseItem.distance_km ??
          retaseItem.km;
        const distanceKm = distanceRaw != null && !isNaN(Number(distanceRaw)) ? Number(Number(distanceRaw).toFixed(2)) : 0;

        const durRaw =
          trip.dur ??
          trip.duration ??
          trip.trip_duration ??
          trip.total_duration ??
          trip.details?.[0]?.dur ??
          trip.details?.[0]?.duration ??
          retaseItem.total_trip_duration ??
          retaseItem.total_duration ??
          retaseItem.trip_duration ??
          retaseItem.dur ??
          retaseItem.duration;
        const durationStr = formatDurationValue(durRaw);

        const dataRow = worksheet.addRow([
          rowCounter++,
          dateStr,
          vehicleStr,
          retaseNumber,
          tripNumber,
          startTimeStr,
          endTimeStr,
          originStr,
          destStr,
          distanceKm,
          durationStr
        ]);
        dataRow.height = 20;

        const isZebra = dIdx % 2 === 1;
        dataRow.eachCell((cell, colNumber) => {
          cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT };
          cell.border = BORDER_THIN;
          if (isZebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ZEBRA_BG } };

          if ([1, 2, 4, 5, 6, 7].includes(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'center' };
          else if ([10, 11].includes(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'right' };
          else cell.alignment = { vertical: 'middle', horizontal: 'left' };

          if (colNumber === 10 && typeof cell.value === 'number') {
            cell.numFmt = '#,##0.00';
          }
        });
      });
    });
  });

  if (totalRetaseRows === 0) {
    const emptyRow = worksheet.addRow(['Tidak ada data retase pada periode ini']);
    worksheet.mergeCells(`A${emptyRow.number}:K${emptyRow.number}`);
    emptyRow.getCell(1).font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT, italic: true, color: { argb: COLOR_MUTED } };
    emptyRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
    emptyRow.getCell(1).border = BORDER_THIN;
  }

  const lastDataRow = worksheet.lastRow ? worksheet.lastRow.number : 8;

  worksheet.autoFilter = `A8:K${Math.max(lastDataRow, 8)}`;
  applyWorksheetConfig(worksheet, 0, 8);

  worksheet.columns = [
    { width: 6 }, // No
    { width: 15 }, // Tanggal
    { width: 18 }, // Vehicle
    { width: 12 }, // Retase Ke
    { width: 10 }, // Trip Ke
    { width: 22 }, // Waktu Mulai
    { width: 22 }, // Waktu Selesai
    { width: 25 }, // Asal
    { width: 25 }, // Tujuan
    { width: 14 }, // Jarak
    { width: 14 } // Durasi
  ];
}

/**
 * Build 1 Complete ExcelJS Workbook for a Driver Detail (4 sheets)
 * @param {Object} row
 * @param {Object} headerInfo
 * @returns {ExcelJS.Workbook}
 */
export function buildDriverDetailWorkbook(row, headerInfo) {
  const workbook = new ExcelJS.Workbook();
  const driverInfo = {
    code: row.driver_code || row.driver_driver_code || row.driver || '-',
    name: row.driver_name || row.driver_driver_name || '-'
  };

  // Sheet 1: Summary (1 row for this driver)
  buildSummarySheet(workbook, [row], headerInfo, driverInfo);

  // Sheet 2: Detail Score Event
  buildDetailScoreEventSheet(workbook, row, headerInfo, driverInfo);

  // Sheet 3: Ringkasan Harian
  buildDailySummarySheet(workbook, row, headerInfo, driverInfo);

  // Sheet 4: Retase
  buildRetaseSheet(workbook, row, headerInfo, driverInfo);

  return workbook;
}

// ==========================================
// WORKBOOK 2 BUILDERS: DRIVER EVIDENCE
// ==========================================

/**
 * Collect all polygons from events and daily breakdown
 * @param {Object} row
 * @returns {Array}
 */
function collectAllPolygons(row) {
  const allEvents = [];
  if (Array.isArray(row.events)) allEvents.push(...row.events);
  if (Array.isArray(row.events_retase)) allEvents.push(...row.events_retase);
  if (Array.isArray(row.events_non_retase)) allEvents.push(...row.events_non_retase);

  if (Array.isArray(row._dailyBreakdown)) {
    row._dailyBreakdown.forEach((d) => {
      if (Array.isArray(d.events)) allEvents.push(...d.events);
    });
  }

  const seen = new Set();
  const result = [];

  allEvents.forEach((ev) => {
    // 1. Pools
    if (Array.isArray(ev?.pools)) {
      ev.pools.forEach((p) => {
        const id = p.id ?? p.pool_id ?? '-';
        const minSpeed = p.min_speed ?? 0;
        const maxSpeed = p.max_speed ?? 0;
        const dedupeKey = `Pool_${id}_${minSpeed}_${maxSpeed}`;
        if (!seen.has(dedupeKey)) {
          seen.add(dedupeKey);
          result.push({
            type: 'Pool',
            typeOrder: 1,
            id,
            minSpeed,
            maxSpeed,
            speedLimit: `${minSpeed} - ${maxSpeed} km/j`
          });
        }
      });
    }

    // 2. Geofences
    if (Array.isArray(ev?.geofences)) {
      ev.geofences.forEach((g) => {
        const id = g.id ?? g.geofence_id ?? '-';
        const minSpeed = g.min_speed ?? 0;
        const maxSpeed = g.max_speed ?? 0;
        const dedupeKey = `Geofence_${id}_${minSpeed}_${maxSpeed}`;
        if (!seen.has(dedupeKey)) {
          seen.add(dedupeKey);
          result.push({
            type: 'Geofence',
            typeOrder: 2,
            id,
            minSpeed,
            maxSpeed,
            speedLimit: `${minSpeed} - ${maxSpeed} km/j`
          });
        }
      });
    }

    // 3. Transit Points
    if (Array.isArray(ev?.transit_points)) {
      ev.transit_points.forEach((tp) => {
        const id = tp.id ?? tp.transit_point_id ?? '-';
        const minSpeed = tp.min_speed ?? 0;
        const maxSpeed = tp.max_speed ?? 0;
        const dedupeKey = `TransitPoint_${id}_${minSpeed}_${maxSpeed}`;
        if (!seen.has(dedupeKey)) {
          seen.add(dedupeKey);
          result.push({
            type: 'Transit Point',
            typeOrder: 3,
            id,
            minSpeed,
            maxSpeed,
            speedLimit: `${minSpeed} - ${maxSpeed} km/j`
          });
        }
      });
    }
  });

  result.sort((a, b) => {
    if (a.typeOrder !== b.typeOrder) return a.typeOrder - b.typeOrder;
    return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
  });

  return result;
}

/**
 * Build Evidence Workbook for a Driver
 * @param {Object} row
 * @param {Object} headerInfo
 * @returns {ExcelJS.Workbook}
 */
export function buildDriverEvidenceWorkbook(row, headerInfo) {
  const workbook = new ExcelJS.Workbook();
  const driverInfo = {
    code: row.driver_code || row.driver_driver_code || row.driver || '-',
    name: row.driver_name || row.driver_driver_name || '-'
  };

  // 1. Sheet 1: "Polygon" (Always present)
  const polygonSheet = workbook.addWorksheet('Polygon');
  const polygonHeaders = ['No', 'Tipe Polygon', 'ID', 'Min Speed (km/j)', 'Max Speed (km/j)', 'Batas Kecepatan'];

  addReportHeader(polygonSheet, 'Data Polygon', headerInfo, driverInfo, polygonHeaders.length);
  applyTableHeader(polygonSheet, polygonHeaders, 8);

  const polygons = collectAllPolygons(row);

  if (polygons.length === 0) {
    const emptyRow = polygonSheet.addRow(['Tidak ada data polygon']);
    polygonSheet.mergeCells(`A${emptyRow.number}:F${emptyRow.number}`);
    emptyRow.getCell(1).font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT, italic: true, color: { argb: COLOR_MUTED } };
    emptyRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
    emptyRow.getCell(1).border = BORDER_THIN;
  } else {
    polygons.forEach((p, idx) => {
      const dataRow = polygonSheet.addRow([idx + 1, p.type, p.id, p.minSpeed, p.maxSpeed, p.speedLimit]);
      dataRow.height = 20;

      const isZebra = idx % 2 === 1;
      dataRow.eachCell((cell, colNumber) => {
        cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT };
        cell.border = BORDER_THIN;
        if (isZebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ZEBRA_BG } };

        if ([1, 2, 3, 6].includes(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'center' };
        else cell.alignment = { vertical: 'middle', horizontal: 'right' };
      });
    });
  }

  const lastPolygonRow = polygonSheet.lastRow ? polygonSheet.lastRow.number : 8;
  polygonSheet.autoFilter = `A8:F${Math.max(lastPolygonRow, 8)}`;
  applyWorksheetConfig(polygonSheet, 0, 8);

  polygonSheet.columns = [
    { width: 6 }, // No
    { width: 18 }, // Tipe
    { width: 12 }, // ID
    { width: 18 }, // Min Speed
    { width: 18 }, // Max Speed
    { width: 22 } // Batas Kecepatan
  ];

  // 2. Subsequent Sheets: 1 Sheet per Date with Evidence
  const allGpsEvidence = collectGpsEvidenceRows(row);
  const allVssEvidence = collectVssEvidenceRows(row);

  // Group evidence by date
  const dateMap = new Map();

  allGpsEvidence.forEach((item) => {
    if (!dateMap.has(item.date)) {
      dateMap.set(item.date, { gps: [], vss: [], rawTime: item._rawTime });
    }
    dateMap.get(item.date).gps.push(item);
  });

  allVssEvidence.forEach((item) => {
    if (!dateMap.has(item.date)) {
      dateMap.set(item.date, { gps: [], vss: [], rawTime: item._rawTime });
    }
    dateMap.get(item.date).vss.push(item);
  });

  const sortedDates = Array.from(dateMap.entries()).sort((a, b) => a[1].rawTime - b[1].rawTime);

  sortedDates.forEach(([dateStr, data]) => {
    const gpsList = data.gps;
    const vssList = data.vss;

    // If both GPS and Camera are empty, do not create sheet
    if (gpsList.length === 0 && vssList.length === 0) return;

    // Excel sheet name max 31 characters
    const sheetName = sanitizeFilenamePart(dateStr).slice(0, 31) || dateStr;
    const dateSheet = workbook.addWorksheet(sheetName);

    // Report Header on Date Sheet (7 columns max)
    addReportHeader(dateSheet, `Evidence - ${dateStr}`, headerInfo, driverInfo, 7);

    const MAX_ROWS = 5000;

    // --- TABLE 1: GPS EVIDENCE ---
    const gpsSectionHeader = dateSheet.addRow(['GPS Evidence']);
    gpsSectionHeader.getCell(1).font = { name: FONT_NAME, size: FONT_SIZE_SECTION, bold: true };
    gpsSectionHeader.height = 24;

    const gpsHeaders = ['No', 'Event', 'Start Time', 'End Time', 'Lokasi', 'Speed', 'Durasi'];
    const gpsHeaderRow = dateSheet.addRow(gpsHeaders);
    gpsHeaderRow.height = 31.95;
    gpsHeaderRow.eachCell((cell) => {
      cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT, bold: true, color: { argb: COLOR_HEADER_FG } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_HEADER_BG } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = BORDER_THIN;
    });

    if (gpsList.length === 0) {
      const emptyRow = dateSheet.addRow(['Tidak ada data GPS Evidence']);
      dateSheet.mergeCells(`A${emptyRow.number}:G${emptyRow.number}`);
      emptyRow.getCell(1).font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT, italic: true, color: { argb: COLOR_MUTED } };
      emptyRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
      emptyRow.getCell(1).border = BORDER_THIN;
    } else {
      const visibleGps = gpsList.slice(0, MAX_ROWS);
      visibleGps.forEach((g, idx) => {
        const dataRow = dateSheet.addRow([idx + 1, g.event, g.startTime, g.endTime, g.location, g.speed, g.duration]);
        dataRow.height = 20;

        const isZebra = idx % 2 === 1;
        dataRow.eachCell((cell, colNumber) => {
          cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT };
          cell.border = BORDER_THIN;
          if (isZebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ZEBRA_BG } };

          if ([1, 3, 4].includes(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'center' };
          else if ([6, 7].includes(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'right' };
          else cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });
      });

      if (gpsList.length > MAX_ROWS) {
        const noteRow = dateSheet.addRow(['Data terpotong (maksimal 5.000 baris ditampilkan), lihat detail lengkap di dashboard']);
        noteRow.getCell(1).font = { name: FONT_NAME, size: 9, italic: true, color: { argb: COLOR_MUTED } };
      }
    }

    // Blank row between tables
    dateSheet.addRow([]);

    // --- TABLE 2: CAMERA EVIDENCE ---
    const cameraSectionHeader = dateSheet.addRow(['Camera Evidence']);
    cameraSectionHeader.getCell(1).font = { name: FONT_NAME, size: FONT_SIZE_SECTION, bold: true };
    cameraSectionHeader.height = 24;

    const cameraHeaders = ['No', 'Event', 'Start Time', 'Vehicle', 'Violation'];
    const cameraHeaderRow = dateSheet.addRow(cameraHeaders);
    cameraHeaderRow.height = 31.95;
    cameraHeaderRow.eachCell((cell) => {
      cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT, bold: true, color: { argb: COLOR_HEADER_FG } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_HEADER_BG } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = BORDER_THIN;
    });

    if (vssList.length === 0) {
      const emptyRow = dateSheet.addRow(['Tidak ada data Camera Evidence']);
      dateSheet.mergeCells(`A${emptyRow.number}:E${emptyRow.number}`);
      emptyRow.getCell(1).font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT, italic: true, color: { argb: COLOR_MUTED } };
      emptyRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
      emptyRow.getCell(1).border = BORDER_THIN;
    } else {
      const visibleVss = vssList.slice(0, MAX_ROWS);
      visibleVss.forEach((v, idx) => {
        const dataRow = dateSheet.addRow([idx + 1, v.event, v.startTime, v.vehicle, v.violation]);
        dataRow.height = 20;

        const isZebra = idx % 2 === 1;
        dataRow.eachCell((cell, colNumber) => {
          cell.font = { name: FONT_NAME, size: FONT_SIZE_DEFAULT };
          cell.border = BORDER_THIN;
          if (isZebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ZEBRA_BG } };

          if ([1, 3, 4].includes(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'center' };
          else cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });
      });

      if (vssList.length > MAX_ROWS) {
        const noteRow = dateSheet.addRow(['Data terpotong (maksimal 5.000 baris ditampilkan), lihat detail lengkap di dashboard']);
        noteRow.getCell(1).font = { name: FONT_NAME, size: 9, italic: true, color: { argb: COLOR_MUTED } };
      }
    }

    applyWorksheetConfig(dateSheet, 0, 0);

    dateSheet.columns = [
      { width: 6 }, // No
      { width: 22 }, // Event
      { width: 22 }, // Start Time
      { width: 22 }, // End Time
      { width: 26 }, // Lokasi / Vehicle
      { width: 16 }, // Speed / Violation
      { width: 14 } // Durasi
    ];
  });

  return workbook;
}

// ==========================================
// PUBLIC EXPORT FUNCTIONS
// ==========================================

/**
 * 1. Export Driver Score Summary as CSV
 * @param {Array} filteredRows
 * @param {Array} columns
 * @param {Object} headerInfo
 */
export function exportDriverScoreCSV(filteredRows, columns, headerInfo) {
  const headerRows = [
    ['Driver Score Summary'],
    ['Company', headerInfo?.companyNames || '-'],
    ['Periode', headerInfo?.period || '-'],
    ['Diekspor', headerInfo?.exportDate || '-'],
    []
  ];

  const exportHeaders = SUMMARY_COLUMNS.map((c) => c.header);
  const dataRows = (filteredRows || []).map((row, idx) => extractSummaryRowData(row, idx, headerInfo));

  const csvString = Papa.unparse(headerRows.concat([exportHeaders]).concat(dataRows));
  // Add UTF-8 BOM so Excel opens CSV properly
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvString], { type: 'text/csv;charset=utf-8;' });
  const filename = `${buildExportFilename('summary', filteredRows, headerInfo)}.csv`;
  downloadBlob(blob, filename);
}

/**
 * 2. Export Driver Score Summary as Excel (.xlsx)
 * @param {Array} filteredRows
 * @param {Array} columns
 * @param {Object} headerInfo
 */
export async function exportDriverScoreExcelSummary(filteredRows, columns, headerInfo) {
  const workbook = new ExcelJS.Workbook();
  buildSummarySheet(workbook, filteredRows || [], headerInfo, null);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const filename = `${buildExportFilename('summary', filteredRows, headerInfo)}.xlsx`;
  downloadBlob(blob, filename);
}

/**
 * 3. Export Driver Score Summary as PDF
 * @param {Array} filteredRows
 * @param {Array} columns
 * @param {Object} headerInfo
 */
export function exportDriverScorePDF(filteredRows, columns, headerInfo) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

  // Title & Header Info
  doc.setFont(FONT_NAME, 'bold');
  doc.setFontSize(14);
  doc.text('Driver Score Summary', 40, 40);

  doc.setFont(FONT_NAME, 'normal');
  doc.setFontSize(9);
  doc.text(`Company : ${headerInfo?.companyNames || '-'}`, 40, 60);
  doc.text(`Periode  : ${headerInfo?.period || '-'}`, 40, 75);
  doc.text(`Diekspor : ${headerInfo?.exportDate || '-'}`, 40, 90);

  const tableHeaders = [SUMMARY_COLUMNS.map((c) => c.header)];
  const tableRows = (filteredRows || []).map((row, idx) => {
    const rawValues = extractSummaryRowData(row, idx, headerInfo);
    return rawValues.map((v) => (v != null ? String(v) : '-'));
  });

  autoTable(doc, {
    startY: 105,
    head: tableHeaders,
    body: tableRows,
    theme: 'grid',
    styles: {
      font: FONT_NAME,
      fontSize: 7.5,
      cellPadding: 4,
      textColor: [0, 0, 0],
      lineColor: [201, 206, 220],
      lineWidth: 0.5
    },
    headStyles: {
      fillColor: [63, 78, 140], // #3F4E8C
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 20 },
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { fontStyle: 'bold' },
      6: { halign: 'right' },
      7: { halign: 'center' },
      8: { halign: 'right' },
      9: { halign: 'center' },
      10: { halign: 'right' },
      11: { halign: 'center' },
      12: { halign: 'right' },
      13: { halign: 'center' },
      14: { halign: 'center' }
    }
  });

  const filename = `${buildExportFilename('summary', filteredRows, headerInfo)}.pdf`;
  doc.save(filename);
}

/**
 * 4. Export Driver Score Detail (Always ZIP structure)
 * ZIP Structure:
 * driver-score-detail_<N>Drivers_<YYYYMMDD-HHmm>.zip
 * ├── driver-score_<company>_<timestamp>.xlsx        <- Summary all drivers
 * ├── <driver_code>_<driver_name>/                   <- 1 folder per driver row
 * │   ├── driver-score-detail_<code>_<name>_<timestamp>.xlsx
 * │   └── driver-score-evidence_<code>_<name>_<timestamp>.xlsx
 * └── ...
 * @param {Array} rows (filteredRows)
 * @param {Object} headerInfo
 * @param {{ onProgress?: (completed: number, total: number) => void }} options
 */
export async function exportDriverScoreExcelDetail(rows, headerInfo, { onProgress } = {}) {
  const exportTimestamp = dayjs().format('YYYYMMDD-HHmm');
  const validRows = rows || [];
  const totalRows = validRows.length;
  if (totalRows === 0) return;

  // 1 summary file + 2 files per driver row
  const totalFiles = 1 + totalRows * 2;
  let completedFiles = 0;

  const zip = new JSZip();

  // Step 1: Generate Summary Workbook at ZIP root
  const summaryWorkbook = new ExcelJS.Workbook();
  buildSummarySheet(summaryWorkbook, validRows, headerInfo, null);
  const summaryBuffer = await summaryWorkbook.xlsx.writeBuffer();
  const summaryFilename = `${buildExportFilename('summary', validRows, headerInfo, exportTimestamp)}.xlsx`;
  zip.file(summaryFilename, summaryBuffer);

  completedFiles++;
  if (typeof onProgress === 'function') {
    onProgress(completedFiles, totalFiles);
  }
  await new Promise((r) => setTimeout(r, 0));

  // Track folder names to handle duplicates (e.g. same driver with multiple rows)
  const usedFolderNames = new Map();

  for (let i = 0; i < totalRows; i++) {
    const row = validRows[i];

    const rawCode = sanitizeFilenamePart(row.driver_code || row.driver_driver_code || row.driver || 'code');
    const rawName = sanitizeFilenamePart(row.driver_name || row.driver_driver_name || 'driver');
    const baseFolderName = `${rawCode}_${rawName}`;

    let folderName = baseFolderName;
    if (usedFolderNames.has(baseFolderName)) {
      const count = usedFolderNames.get(baseFolderName) + 1;
      usedFolderNames.set(baseFolderName, count);
      folderName = `${baseFolderName}_${count}`;
    } else {
      usedFolderNames.set(baseFolderName, 1);
    }

    // Step 2: Build Driver Detail Workbook (4 sheets)
    const detailWorkbook = buildDriverDetailWorkbook(row, headerInfo);
    const detailBuffer = await detailWorkbook.xlsx.writeBuffer();
    const detailFilename = `${buildExportFilename('detail-single', [row], headerInfo, exportTimestamp)}.xlsx`;
    zip.file(`${folderName}/${detailFilename}`, detailBuffer);

    completedFiles++;
    if (typeof onProgress === 'function') {
      onProgress(completedFiles, totalFiles);
    }
    await new Promise((r) => setTimeout(r, 0));

    // Step 3: Build Driver Evidence Workbook (Polygon + date sheets)
    const evidenceWorkbook = buildDriverEvidenceWorkbook(row, headerInfo);
    const evidenceBuffer = await evidenceWorkbook.xlsx.writeBuffer();
    const evidenceFilename = `${buildExportFilename('evidence-single', [row], headerInfo, exportTimestamp)}.xlsx`;
    zip.file(`${folderName}/${evidenceFilename}`, evidenceBuffer);

    completedFiles++;
    if (typeof onProgress === 'function') {
      onProgress(completedFiles, totalFiles);
    }
    await new Promise((r) => setTimeout(r, 0));
  }

  // Generate ZIP with STORE compression (xlsx files are already compressed zip archives)
  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'STORE'
  });

  const zipFilename = `${buildExportFilename('detail', validRows, headerInfo, exportTimestamp)}.zip`;
  downloadBlob(zipBlob, zipFilename);
}
